import type { ComboVariation, PropostaItem } from '@/types'
import { calculateAllVariations, calculateDealTotal } from '@/utils/dealCalculations'
import type { ComboValue } from './types'
import { calculateComboDiscountTotal } from './comboPricing'

export interface ComboLine {
  id: string
  label: string
  items: PropostaItem[]
  subtotal: number
  total: number
  desconto: number
}

export interface VariationLine {
  id: string
  label: string
  items: PropostaItem[]
  valorOriginal: number
  valorFinal: number
  desconto: number
  /** Total da opção + itens fora de qualquer opção (cenário completo) */
  scenarioTotal: number
}

export type ProposalDisplayMode = 'all_items' | 'min_variation'

export interface ProposalTotalsBreakdown {
  comboLines: ComboLine[]
  variationLines: VariationLine[]
  lineItems: PropostaItem[]
  lineItemsSubtotal: number
  mktItemsCount: number
  mktSelectedItem: PropostaItem | null
  desconto: number
  valorDesconto: number
  comboDiscountTotal: number
  subtotal: number
  /** Valor exibido e usado ao salvar */
  displayTotal: number
  displayMode: ProposalDisplayMode
  minVariationLabel: string | null
  /** Soma de todos os itens (referência quando há opções) */
  allItemsTotal: number
}

const MKT_KEYWORDS = ['mkt', 'marketing', 'tráfego', 'trafego', 'ads', 'mídia', 'midia']

function isMKTItem(item: PropostaItem): boolean {
  const searchText = [item.descricao, item.nome].filter(Boolean).join(' ').toLowerCase()
  return MKT_KEYWORDS.some((kw) => searchText.includes(kw))
}

function lineFromManualCombo(cv: ComboValue): ComboLine {
  const subtotal = cv.subtotal
  const total = cv.total
  return {
    id: cv.id,
    label: cv.nome,
    items: cv.items,
    subtotal,
    total,
    desconto: Math.max(0, subtotal - total),
  }
}

function mktHintFromItems(itens: PropostaItem[]) {
  const mktItems = itens.filter(isMKTItem)
  const mktSelectedItem =
    mktItems.length > 0
      ? mktItems.reduce((min, item) => (item.total < min.total ? item : min))
      : null
  return { mktItemsCount: mktItems.length, mktSelectedItem }
}

export function buildProposalTotalsBreakdown(
  itens: PropostaItem[],
  comboValues: ComboValue[],
  comboVariations: ComboVariation[],
  desconto: number,
): ProposalTotalsBreakdown {
  const comboDiscountTotal = calculateComboDiscountTotal(comboValues)
  const manualCombos = comboValues.filter((c) => c.items.length > 0)
  const comboLines = manualCombos.map(lineFromManualCombo)

  const inManualCombo = new Set<string>()
  for (const cv of manualCombos) {
    cv.items.forEach((i) => inManualCombo.add(i.id))
  }

  const pricedVariations = calculateAllVariations(
    comboVariations.filter((v) => v.itemIds.length > 0),
    itens,
  )

  const inAnyVariation = new Set(pricedVariations.flatMap((v) => v.itemIds))

  const orphanItems = itens.filter(
    (item) => !inManualCombo.has(item.id) && !inAnyVariation.has(item.id),
  )
  const orphanCalc = calculateDealTotal(orphanItems, 0)
  const orphanTotal = orphanCalc.valorTotal

  const allItemsCalc = calculateDealTotal(itens, desconto)
  const allItemsTotal = allItemsCalc.valorTotal - comboDiscountTotal

  if (pricedVariations.length > 0) {
    const manualCombosTotal = comboLines.reduce((sum, line) => sum + line.total, 0)

    const variationLines: VariationLine[] = pricedVariations.map((v) => {
      const scenarioTotal = v.valorFinal + orphanTotal + manualCombosTotal
      return {
        id: v.id,
        label: v.nome,
        items: v.items,
        valorOriginal: v.valorOriginal,
        valorFinal: v.valorFinal,
        desconto: Math.max(0, v.valorOriginal - v.valorFinal),
        scenarioTotal,
      }
    })

    const minVariation = variationLines.reduce((min, line) =>
      line.scenarioTotal < min.scenarioTotal ? line : min,
    )

    const subtotal = minVariation.scenarioTotal
    const valorDesconto = subtotal * (desconto / 100)
    const displayTotal = Math.max(0, subtotal - valorDesconto - comboDiscountTotal)

    const mkt = mktHintFromItems(itens)

    return {
      comboLines,
      variationLines,
      lineItems: orphanItems,
      lineItemsSubtotal: orphanTotal,
      ...mkt,
      desconto,
      valorDesconto,
      comboDiscountTotal,
      subtotal,
      displayTotal,
      displayMode: 'min_variation',
      minVariationLabel: minVariation.label,
      allItemsTotal,
    }
  }

  const lineItems = itens.filter((item) => !inManualCombo.has(item.id))
  const lineItemsSubtotal = lineItems.reduce((sum, item) => sum + item.total, 0)
  const mkt = mktHintFromItems(itens)

  return {
    comboLines,
    variationLines: [],
    lineItems,
    lineItemsSubtotal,
    ...mkt,
    desconto,
    valorDesconto: allItemsCalc.valorDesconto,
    comboDiscountTotal,
    subtotal: allItemsCalc.subtotal,
    displayTotal: allItemsTotal,
    displayMode: 'all_items',
    minVariationLabel: null,
    allItemsTotal,
  }
}
